import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useVideoTutorial } from '../../../contexts/video-tutorial-context/video-tutorial-context';

// Define the FAQ items here
const faqItems = [
  {
    question: 'What should I do when the labels run out during printing?',
    answer:
      'The printer will emit a beeping sound to indicate that the label roll is empty. Simply replace it with a new roll, and the printer will automatically resume printing from where it left off.',
  },
  {
    question: 'How can I connect the printer to my device?',
    answer:
      'Enable Bluetooth on your device, then search for “BT-802” or “TSC-802.” Select the printer from the list and enter the PIN “0000” when prompted to complete pairing.',
  },
  {
    question: 'How do I confirm that the printer is connected to the Parcel Tracer mobile app?',
    answer:
      'A solid blue indicator light on the printer confirms that it is successfully connected to the Parcel Tracer mobile application.',
  },
  {
    question: 'How can I get technical support?',
    answer:
      'You can reach our support team by emailing info@parceltracer.app, contacting us via WhatsApp at +961 79 025 627, or by clicking the Help button located at the bottom-right corner of this page.',
  },
];


const LearnPage: React.FC = () => {
  const { showVideoTutorial } = useVideoTutorial();
  const tutorialSrc = 'https://www.youtube.com/embed/WC33c5_GzM4';

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <img
            src="/main-logo.png"
            alt="Company Logo"
            style={{ width: 180, marginBottom: 16 }}
          />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to Your Thermal Printer Setup
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Learn how to set up and get the best from your BT-802 thermal printer.
          </Typography>
        </Box>

        {/* Printer Image */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <img
            src="/bt-802.png"
            alt="BT-802 Printer"
            style={{
              maxWidth: '100%',
              width: 320,
              borderRadius: 12,
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            }}
          />
        </Box>

        {/* Quick Start Section */}
        <Card
          elevation={3}
          sx={{
            mb: 6,
            borderRadius: 3,
            backgroundColor: 'white',
          }}
        >
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Getting Started
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Follow this quick tutorial to connect your printer, load labels, and start printing in minutes.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() => showVideoTutorial(tutorialSrc)}
            >
              Watch Video Tutorial
            </Button>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            textAlign="center"
          >
            Frequently Asked Questions
          </Typography>

          {faqItems.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Footer */}
        <Box textAlign="center" color="text.secondary" mt={4}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Parcel Tracer. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LearnPage;
